import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { VesselMessage } from '../../../../types/VesselMessage';
import { Button } from '../../../cdl/Button/Button';
import { useToasts } from '../../../cdl/Toast/useToasts';
import { updateVessel } from '../../../common/api/clients/vessel.api';
import { sendVesselRequisitionToVessel } from '../../../common/api/clients/vesselRequisition.api';
import { queries } from '../../../common/api/queryKeys/queries';
import { ButtonGroup } from '../../../common/buttons/ButtonGroup';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { IconPlus } from '../../../common/icons/cdl/Plus';
import { RequisitionAction } from '../../../common/VesselRequisition/types/RequisitionAction';
import { RequisitionOptions } from '../../../common/VesselRequisition/types/RequisitionOptions';
import { RequisitionScope } from '../../../common/VesselRequisition/types/RequisitionScope';
import { VesselRequisitionModal } from '../../../common/VesselRequisition/VesselRequisitionModal';
import { useVesselRequisitionDownload } from '../hooks/useVesselRequisitionDownload';

interface VesselActionsProps {
    vessel: VesselMessage;
}

export const VesselActions = ({ vessel }: VesselActionsProps) => {
    const queryClient = useQueryClient();
    const role = useRole();
    const navigate = useNavigate();
    const { addToast } = useToasts();
    const downloadVesselRequisition = useVesselRequisitionDownload();

    const [showRequisitionModal, setShowRequisitionModal] = useState(false);

    const { mutateAsync: executeSendVesselRequisition } = useMutation({
        mutationFn: sendVesselRequisitionToVessel,
    });
    const { mutateAsync: executeUpdateVessel } = useMutation({
        mutationFn: updateVessel,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queries.vessels.detail(vessel.id).queryKey });
        },
    });

    const submitRequisition = async (options: RequisitionOptions, action: RequisitionAction, emailAddress?: string) => {
        if (emailAddress && !vessel.email?.trim()) {
            await executeUpdateVessel({ vesselId: vessel.id, vessel: { ...vessel, email: emailAddress } });
        }
        if (action === RequisitionAction.DOWNLOAD) {
            await downloadVesselRequisition(vessel.id, options);
            return;
        }
        await executeSendVesselRequisition({ id: vessel.id, options });
        addToast(translate('vessel.vesselRequisitionSentToast'));
    };

    const onClickNewEnquiryButton = () => {
        navigate({
            to: '/orders/new',
            state: {
                vesselId: vessel.id,
                customerId: vessel.customerId,
            },
        });
    };

    return (
        <ButtonGroup>
            <Button onClick={() => setShowRequisitionModal(true)}>{translate('vessel.requisitionForm.label')}</Button>

            {role.isCustomer() && vessel.active ? (
                <Button emphasis="high" LeadingIcon={IconPlus} onClick={onClickNewEnquiryButton}>
                    {translate('vessel.newEnquiry')}
                </Button>
            ) : null}

            {showRequisitionModal ? (
                <VesselRequisitionModal
                    scope={RequisitionScope.VESSEL}
                    vesselEmailAddress={vessel.email}
                    onDismiss={() => setShowRequisitionModal(false)}
                    onSubmit={submitRequisition}
                />
            ) : null}
        </ButtonGroup>
    );
};
